import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// Settings → HRX & Bring (guided P&P phase 7, 2026-10-17; backend/hrx_schedule.py): our HRX days, the thresholds, the
// three day types' times, the schedule as read from HRX's e-mails (a line the WMS does not recognise is confirmed here,
// never guessed), pasting an e-mail by hand, and the switch that lets the WMS read the warehouse mailbox.

const card = 'bg-[#1a1d27] rounded-xl border border-gray-800 p-5';
const head = 'text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3';
const input = 'px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white text-sm';
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'numeric' });

export default function HrxSettings() {
  const [data, setData] = useState<any>(null);
  const [pol, setPol] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [paste, setPaste] = useState('');
  const [subject, setSubject] = useState('');
  const load = () => authFetch(`${API}/settings/hrx`).then(async r => {
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setData(d); setPol(d.policy);
  });
  useEffect(() => { load(); }, []);
  if (!data || !pol) return <div className="text-gray-400 text-sm">{err || 'Loading…'}</div>;

  const save = async (extra: any = {}) => {
    setErr(''); setMsg('');
    // read_mail only when its own switch is clicked (switching it on is admin-only; a manager's Save must not resend it)
    const body: any = { ...pol };
    ['read_mail', 'mailbox', 'last_sent_at', 'updated_by'].forEach(k => delete body[k]);
    const r = await authFetch(`${API}/settings/hrx`, { method: 'PUT', body: JSON.stringify({ ...body, ...extra }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setPol(d); setMsg('✓ Saved'); load();
  };
  const sendPaste = async () => {
    const r = await authFetch(`${API}/hrx/emails/paste`, { method: 'POST', body: JSON.stringify({ subject, body: paste }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setMsg(`✓ Read: ${d.days.length} day(s), ${d.status}`); setPaste(''); setSubject(''); load();
  };
  const confirm = async (day: any, t: string) => {
    const r = await authFetch(`${API}/hrx/days/${day.date}/confirm`, { method: 'POST', body: JSON.stringify({ day_type: t, email_id: day.email_id }) });
    if (!r.ok) { setErr(`HTTP ${r.status}`); return; }
    load();
  };
  const types: Record<string, string> = data.types || {};

  return (
    <div className="space-y-6">
      {msg && <div className="text-green-400 text-sm">{msg}</div>}
      {err && <div className="text-red-400 text-sm">{err}</div>}
      <div className={card}>
        <div className={head}>Our HRX days and the rule</div>
        <div className="flex flex-wrap gap-3 mb-3">
          {DAYS.map(d => (
            <label key={d} className="flex items-center gap-1 text-sm text-gray-200">
              <input type="checkbox" checked={!!pol.weekdays[d]} onChange={e => setPol({ ...pol, weekdays: { ...pol.weekdays, [d]: e.target.checked } })} />
              {d.charAt(0).toUpperCase() + d.slice(1)}</label>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-gray-300">
          {[['min_per_country', 'parcels per country'], ['min_countries', 'countries'], ['max_wait_workdays', 'max workdays waiting'],
            ['orders_per_hour', 'orders packed per hour (forecast)']].map(([k, l]) => (
            <label key={k}>{l} <input value={pol[k]} onChange={e => setPol({ ...pol, [k]: e.target.value.replace(/\D/g, '') })}
              className={`${input} keep-width w-16 ml-1`} /></label>
          ))}
        </div>
        <div className="mt-4 text-sm text-gray-300">
          <div className="text-gray-400 mb-1">Packing hours — the forecast counts only these minutes before "goods ready" (empty = no packing that day)</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {DAYS.map(d => {
              const w: string[] | null = pol.packing_hours?.[d] || null;
              const set = (i: number, v: string) => {
                const n = [...(w || ['', ''])]; n[i] = v;
                setPol({ ...pol, packing_hours: { ...pol.packing_hours, [d]: n[0] || n[1] ? n : null } });
              };
              return (
                <label key={d} className="flex items-center gap-1">
                  <span className="w-9">{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  <input value={w?.[0] || ''} onChange={e => set(0, e.target.value)} placeholder="—" className={`${input} keep-width w-16`} />
                  –<input value={w?.[1] || ''} onChange={e => set(1, e.target.value)} placeholder="—" className={`${input} keep-width w-16`} />
                </label>
              );
            })}
          </div>
        </div>
        <div className="mt-4 space-y-1 text-sm text-gray-300">
          {Object.keys(pol.day_types).map(t => (
            <div key={t} className="flex flex-wrap items-center gap-3">
              <span className="w-56">{types[t] || t}</span>
              <label>goods ready <input value={pol.day_types[t].ready || ''} onChange={e => setPol({ ...pol, day_types: { ...pol.day_types, [t]: { ...pol.day_types[t], ready: e.target.value } } })}
                className={`${input} keep-width w-16 ml-1`} /></label>
              <label>HRX order by <input value={pol.day_types[t].order_by || ''} onChange={e => setPol({ ...pol, day_types: { ...pol.day_types, [t]: { ...pol.day_types[t], order_by: e.target.value || null } } })}
                className={`${input} keep-width w-16 ml-1`} placeholder="—" /></label>
            </div>
          ))}
          <label className="block mt-2">No e-mail covers a day → treat it as
            <select value={pol.default_when_missing} onChange={e => setPol({ ...pol, default_when_missing: e.target.value })} className={`${input} ml-2`}>
              {['normal', 'via_finland', 'tallinn_late'].map(t => <option key={t} value={t}>{types[t] || t}</option>)}
            </select></label>
        </div>
        <button onClick={() => save()} className="mt-4 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">Save</button>
      </div>

      <div className={card}>
        <div className={head}>Schedule from HRX's e-mails</div>
        <div className="space-y-1 text-sm">
          {data.days.map((d: any) => (
            <div key={d.date} className="flex flex-wrap items-center gap-3 text-gray-300">
              <span className="w-28">{day(d.date)}</span>
              <span className={d.type === 'unknown' ? 'text-amber-300' : d.type === 'no_ship' ? 'text-gray-500' : 'text-gray-100'}>
                {types[d.type] || d.type}{d.source === 'default' ? ' (default)' : ''}</span>
              {!d.policy_day && <span className="text-xs text-gray-500">not our HRX day</span>}
              {d.type === 'unknown' && (
                <>
                  <span className="text-xs text-gray-400">“{d.raw}”</span>
                  <select defaultValue="" onChange={e => e.target.value && confirm(d, e.target.value)} className={input}>
                    <option value="">this means…</option>
                    {['normal', 'via_finland', 'tallinn_late'].map(t => <option key={t} value={t}>{types[t]}</option>)}
                  </select>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="text-xs text-gray-500 mt-3">E-mails read: {data.emails.map((e: any) => `${e.subject?.slice(0, 60)} (${e.parse_status})`).join(' · ') || 'none yet'}</div>
      </div>

      <div className={card}>
        <div className={head}>Paste an HRX e-mail</div>
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="subject (optional)" className={`${input} w-full mb-2`} />
        <textarea value={paste} onChange={e => setPaste(e.target.value)} rows={6} placeholder="Lugupeetud partner, … 31.08-06.09 nädal 36 …" className={`${input} w-full`} />
        <button onClick={sendPaste} disabled={paste.trim().length < 20} className="mt-2 px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40">Read it</button>
        <label className="flex items-center gap-2 text-sm text-gray-200 mt-4">
          <input type="checkbox" checked={!!pol.read_mail} onChange={e => save({ read_mail: e.target.checked })} />
          Read HRX's e-mails from {pol.mailbox} automatically (every 15 minutes; one search, and each new e-mail once)
        </label>
      </div>
    </div>
  );
}
